<template>
  <Drawer
    v-model:visible="visible"
    position="right"
    :header="t('quoteDrawer.title')"
    class="!w-full md:!max-w-[420px]"
    :pt="{ root: { class: 'general-drawer' } }"
    @hide="resetForm"
  >
    <div
      class="flex flex-col gap-4 h-full mt-1 [&_.p-inputtext]:text-base [&_.p-inputtext]:md:text-sm [&_.p-select-label]:text-base [&_.p-select-label]:md:text-sm"
    >
      <div class="flex flex-col gap-1.5">
        <InputText
          v-model="form.companyName"
          :placeholder="t('enquiryForm.companyName')"
          :invalid="!!errors.companyName"
          class="!text-sm"
          fluid
          @input="errors.companyName = ''"
        />
        <small v-if="errors.companyName" class="text-red-400 text-xs pl-1">{{ errors.companyName }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <InputText
          v-model="form.yourName"
          :placeholder="t('enquiryForm.yourName')"
          :invalid="!!errors.yourName"
          class="!text-sm"
          fluid
          @input="errors.yourName = ''"
        />
        <small v-if="errors.yourName" class="text-red-400 text-xs pl-1">{{ errors.yourName }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <InputText
          v-model="form.email"
          :placeholder="t('enquiryForm.email')"
          :invalid="!!errors.email"
          class="!text-sm"
          type="email"
          fluid
          @input="errors.email = ''"
        />
        <small v-if="errors.email" class="text-red-400 text-xs pl-1">{{ errors.email }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <CountrySelectField
          v-model="form.country"
          :invalid="!!errors.country"
          @update:model-value="errors.country = ''"
        />
        <small v-if="errors.country" class="text-red-400 text-xs pl-1">{{ errors.country }}</small>
      </div>

      <div class="flex flex-col gap-1.5">
        <PhoneInputField v-model="form.phone" :invalid="!!errors.phone" @update:model-value="errors.phone = ''" />
        <small v-if="errors.phone" class="text-red-400 text-xs pl-1">{{ errors.phone }}</small>
      </div>

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-gray-600">{{ t('quoteDrawer.parts') }}</label>

        <div v-for="(part, index) in form.parts" :key="index" class="flex gap-2 items-start">
          <div class="flex flex-col gap-1 flex-1">
            <InputText
              v-model="part.partNumber"
              :placeholder="t('enquiryForm.partNumber')"
              :invalid="!!partErrors[index]?.partNumber"
              class="!text-sm"
              fluid
              @input="clearPartError(index, 'partNumber')"
            />
            <small v-if="partErrors[index]?.partNumber" class="text-red-400 text-xs pl-1">
              {{ partErrors[index].partNumber }}
            </small>
          </div>

          <div
            class="inline-flex items-stretch border border-gray-300 rounded-lg overflow-hidden flex-shrink-0 h-[38px]"
          >
            <button
              type="button"
              class="w-8 flex items-center justify-center bg-white hover:bg-gray-50 text-gray-500 border-r border-gray-300"
              @click="part.quantity > 1 && part.quantity--"
            >
              <i class="pi pi-minus text-xs"></i>
            </button>
            <span class="w-8 flex items-center justify-center text-sm font-semibold bg-white text-[#1a233f]">
              {{ part.quantity }}
            </span>
            <button
              type="button"
              class="w-8 flex items-center justify-center bg-white hover:bg-gray-50 text-gray-500 border-l border-gray-300"
              @click="part.quantity++"
            >
              <i class="pi pi-plus text-xs"></i>
            </button>
          </div>

          <button
            v-if="form.parts.length > 1"
            type="button"
            class="flex-shrink-0 w-8 h-[38px] flex items-center justify-center text-red-400 hover:text-red-600 transition-colors"
            @click="removePart(index)"
          >
            <i class="pi pi-trash text-sm"></i>
          </button>
        </div>

        <button
          v-if="form.parts.length < MAX_PARTS"
          type="button"
          class="flex items-center gap-1.5 text-sm text-[#2db9d6] hover:text-[#259cb5] transition-colors mt-1 w-fit"
          @click="addPart"
        >
          <i class="pi pi-plus text-xs"></i>
          {{ t('quoteDrawer.addPart') }}
        </button>
      </div>
    </div>

    <template #footer>
      <AppButton
        :label="t('enquiryForm.send')"
        :loading="submitting"
        size="medium"
        class="w-full justify-center"
        @click="submitForm"
      >
        <template #icon><i class="pi pi-send"></i></template>
      </AppButton>
    </template>
  </Drawer>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import * as yup from 'yup'
import { Drawer, InputText } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { useReCaptcha } from 'vue-recaptcha-v3'
import AppButton from '@/components/general/ui/AppButton.vue'
import CountrySelectField from '@/components/general/ui/CountrySelectField.vue'
import PhoneInputField from '@/components/general/ui/PhoneInputField.vue'
import apiClient from '@/utils/apiClient'

const MAX_PARTS = 10

const visible = defineModel({ type: Boolean, default: false })

const { t, locale } = useI18n({ useScope: 'global' })
const toast = useToast()
const { executeRecaptcha, recaptchaLoaded } = useReCaptcha()

const submitting = ref(false)

const createPart = () => ({ partNumber: '', quantity: 1 })

const form = reactive({
  companyName: '',
  yourName: '',
  email: '',
  country: null,
  phone: '',
  parts: [createPart()],
})

const errors = reactive({
  companyName: '',
  yourName: '',
  email: '',
  country: '',
  phone: '',
})

const partErrors = ref([])

const baseSchema = computed(() =>
  yup.object({
    companyName: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(150, t('validation.tooLong', { max: 150 })),
    yourName: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(100, t('validation.tooLong', { max: 100 })),
    email: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .email(t('validation.emailInvalid'))
      .max(150, t('validation.tooLong', { max: 150 })),
    country: yup.object().nullable().required(t('validation.required')),
    phone: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(30, t('validation.tooLong', { max: 30 })),
  })
)

const partSchema = computed(() =>
  yup.object({
    partNumber: yup
      .string()
      .trim()
      .required(t('validation.required'))
      .max(100, t('validation.tooLong', { max: 100 })),
  })
)

function addPart() {
  if (form.parts.length >= MAX_PARTS) return
  form.parts.push(createPart())
  partErrors.value.push({})
}

function removePart(index) {
  form.parts.splice(index, 1)
  partErrors.value.splice(index, 1)
}

function clearPartError(index, field) {
  if (partErrors.value[index]) {
    partErrors.value[index][field] = ''
  }
}

function clearErrors() {
  Object.keys(errors).forEach((k) => (errors[k] = ''))
  partErrors.value = form.parts.map(() => ({}))
}

async function validateForm() {
  clearErrors()
  let valid = true

  try {
    await baseSchema.value.validate(form, { abortEarly: false })
  } catch (e) {
    if (e instanceof yup.ValidationError) {
      e.inner.forEach((err) => {
        if (err.path && err.path in errors) errors[err.path] = err.message
      })
    }
    valid = false
  }

  for (let i = 0; i < form.parts.length; i++) {
    try {
      await partSchema.value.validate(form.parts[i], { abortEarly: false })
    } catch (e) {
      if (e instanceof yup.ValidationError) {
        e.inner.forEach((err) => {
          if (err.path) {
            if (!partErrors.value[i]) partErrors.value[i] = {}
            partErrors.value[i][err.path] = err.message
          }
        })
      }
      valid = false
    }
  }

  return valid
}

function resetForm() {
  form.companyName = ''
  form.yourName = ''
  form.email = ''
  form.country = null
  form.phone = ''
  form.parts = [createPart()]
  clearErrors()
}

async function submitForm() {
  if (!(await validateForm())) return

  submitting.value = true
  try {
    await recaptchaLoaded()
    const recaptchaToken = await executeRecaptcha('enquiry_form')

    await apiClient.post(
      '/enquiry-form',
      {
        company: form.companyName,
        name: form.yourName,
        email: form.email,
        country: form.country.code,
        phone: form.phone,
        parts: form.parts.map((p) => ({
          partNumber: p.partNumber,
          quantity: p.quantity,
        })),
        recaptchaToken,
      },
      { headers: { 'Accept-Language': locale.value } }
    )

    toast.add({
      severity: 'success',
      summary: t('enquiryForm.successTitle'),
      detail: t('enquiryForm.successDetail'),
      life: 4000,
    })

    visible.value = false
  } catch (error) {
    if (error.response?.status === 422) {
      const backendErrors = error.response.data.errors ?? {}

      if (backendErrors.recaptchaToken) {
        toast.add({
          severity: 'error',
          summary: t('enquiryForm.errorTitle'),
          detail: t('validation.recaptchaError'),
          life: 4000,
        })
        return
      }

      Object.entries(backendErrors).forEach(([key, messages]) => {
        if (key in errors) errors[key] = messages[0]
      })
      return
    }

    toast.add({
      severity: 'error',
      summary: t('enquiryForm.errorTitle'),
      detail: t('enquiryForm.errorDetail'),
      life: 4000,
    })
  } finally {
    submitting.value = false
  }
}
</script>
